import {
  type Cidr,
  contains,
  createTree,
  formatCidr,
  joinInto,
  networkAddress,
  split,
  splitLeaf,
  type SubnetTree,
} from '@it-tools/core-net'

/**
 * Per-subnet annotations the user adds on top of the pure tree.
 *
 * Kept separate from `SubnetTree` so the tree stays pure network math: annotations
 * are keyed by canonical CIDR text, which survives splits and joins elsewhere in
 * the design.
 */
export interface SubnetNote {
  readonly label: string
  /** Token name from the row palette, not a raw colour. See `ROW_COLORS`. */
  readonly color: string
}

export const ROW_COLORS = ['none', 'blue', 'green', 'amber', 'red', 'purple', 'teal'] as const
export type RowColor = (typeof ROW_COLORS)[number]

export interface Design {
  readonly tree: SubnetTree
  readonly notes: Readonly<Record<string, SubnetNote>>
}

export function createDesign(root: Cidr): Design {
  return { tree: createTree(root), notes: {} }
}

export function noteKey(cidr: Cidr): string {
  return formatCidr(cidr)
}

export function getNote(design: Design, cidr: Cidr): SubnetNote {
  return design.notes[noteKey(cidr)] ?? { label: '', color: 'none' }
}

export function setNote(design: Design, cidr: Cidr, patch: Partial<SubnetNote>): Design {
  const key = noteKey(cidr)
  const next = { ...getNote(design, cidr), ...patch }
  const notes = { ...design.notes }
  // Drop empty annotations so they never bloat a shared URL.
  if (next.label === '' && next.color === 'none') delete notes[key]
  else notes[key] = next
  return { tree: design.tree, notes }
}

/**
 * Divides a subnet, carrying its note and colour down to **both** halves.
 *
 * The annotation describes the address space, not the row, so halving a block
 * labelled "DMZ" should leave two halves still recognisably the DMZ. The parent's
 * own entry is then dropped: it is no longer a row, and leaving it behind would
 * quietly pad every shared URL with annotations nobody can see.
 */
export function splitSubnet(design: Design, target: Cidr): Design {
  const tree = splitLeaf(design.tree, target)
  const inherited = design.notes[noteKey(target)]
  if (!inherited) return { tree, notes: design.notes }

  const notes = { ...design.notes }
  delete notes[noteKey(target)]
  for (const half of split(target)) notes[noteKey(half)] = inherited
  return { tree, notes }
}

/**
 * Collapses `block` into one leaf. Annotations of the disappearing children are
 * dropped — keeping them would resurrect stale labels on a later re-split.
 *
 * `block` must be an ANCESTOR, not a leaf: a leaf contains only itself, so passing
 * one here is a silent no-op. The bracket emits ancestor blocks for exactly this
 * reason — see `bracketLayout` in core-net.
 */
export function joinSubnet(design: Design, block: Cidr): Design {
  const tree = joinInto(design.tree, block)
  if (tree === design.tree) return design

  const survivors = new Set(tree.leaves.map(noteKey))
  const notes: Record<string, SubnetNote> = {}
  for (const [key, note] of Object.entries(design.notes)) {
    if (survivors.has(key)) notes[key] = note
  }

  // Mirror of the split rule: the merged block inherits from its first child, so
  // annotate -> split -> join returns the label instead of silently dropping it.
  const blockKey = noteKey(block)
  if (!notes[blockKey]) {
    const firstChild = design.tree.leaves.find(
      (leaf) => contains(block, leaf) && networkAddress(leaf) === networkAddress(block),
    )
    const inherited = firstChild && design.notes[noteKey(firstChild)]
    if (inherited) notes[blockKey] = inherited
  }
  return { tree, notes }
}

/** Rebases the design onto a new root network, discarding the old division. */
export function changeRoot(design: Design, root: Cidr): Design {
  const same =
    networkAddress(design.tree.root) === networkAddress(root) &&
    design.tree.root.prefix === root.prefix
  return same ? design : createDesign(root)
}
